import ExcelJS from "exceljs";
import { resolveScheduleAuditDisplay } from "./scheduleAuditLabels";

export const SCHEDULE_ACTIVITY_EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

export const SCHEDULE_ACTIVITY_DATE_PRESETS = [
  "Today",
  "Yesterday",
  "Last 7 Days",
  "Last 30 Days",
  "Last 90 Days",
  "This Year",
  "All Time",
  "Custom",
];

export const SCHEDULE_ACTIVITY_EMPTY_FILTERS = {
  query: "",
  fields: [],
  users: [],
  dateRange: { ...SCHEDULE_ACTIVITY_EMPTY_DATE_RANGE },
};

const STATIC_FIELD_OPTIONS = [
  "Schedule setup",
  "E-communication",
  "Schedule Visit",
  "Schedule groups",
  "Schedule status",
  "Schedule",
];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function parseFilterDate(value) {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatScheduleAuditWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  let hours = date.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day}/${month}/${year} ${pad(hours)}:${pad(date.getMinutes())} ${ampm}`;
}

export function formatNotificationStatus(label, value) {
  const text = String(value ?? "").trim();
  const enabled =
    text.toLowerCase() === "on" || text.toLowerCase() === "yes" || text === "true";
  if (label === "Email") {
    return enabled
      ? "Participants will receive email messages"
      : "Email messages are turned off";
  }
  if (label === "SMS") {
    return enabled
      ? "Participants will receive text messages"
      : "Text messages are turned off";
  }
  return text || "—";
}

export function getScheduleAuditUserOptions(auditLog = []) {
  const users = new Set();
  auditLog.forEach((row) => {
    const name = String(row?.UserName || "").trim();
    if (name) users.add(name);
  });
  return Array.from(users)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name }));
}

export function getScheduleAuditFieldOptions(auditLog = []) {
  const fields = new Set(STATIC_FIELD_OPTIONS);
  auditLog.forEach((row) => {
    const field = resolveScheduleAuditDisplay(row).fieldDisplay;
    if (field && field !== "—") fields.add(field);
  });
  return Array.from(fields)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
}

function matchesDateRange(at, range = SCHEDULE_ACTIVITY_EMPTY_DATE_RANGE) {
  if (!range || range.preset === "All Time" || (!range.startDate && !range.endDate)) {
    return true;
  }
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return false;

  const start = range.startDate ? startOfDay(parseFilterDate(range.startDate)) : null;
  const end = range.endDate ? endOfDay(parseFilterDate(range.endDate)) : null;
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
}

export function filterScheduleAuditLog(auditLog = [], filters = SCHEDULE_ACTIVITY_EMPTY_FILTERS) {
  const query = String(filters.query || "")
    .trim()
    .toLowerCase();
  const selectedFields = Array.isArray(filters.fields) ? filters.fields : [];
  const selectedUsers = Array.isArray(filters.users) ? filters.users : [];

  return auditLog.filter((row) => {
    const display = resolveScheduleAuditDisplay(row);

    if (selectedFields.length > 0 && !selectedFields.includes(display.fieldDisplay)) {
      return false;
    }
    if (selectedUsers.length > 0 && !selectedUsers.includes(display.actorLabel)) {
      return false;
    }
    if (!matchesDateRange(row?.At, filters.dateRange)) {
      return false;
    }
    if (!query) return true;

    const haystack = [
      display.actionDisplay,
      display.fieldDisplay,
      display.detailDisplay,
      display.reasonDisplay,
      display.oldDisplay,
      display.newDisplay,
      ...(Array.isArray(display.valueDiffRows)
        ? display.valueDiffRows.flatMap((diffRow) => [
            diffRow.label,
            diffRow.oldValue,
            diffRow.newValue,
          ])
        : []),
      display.actorLabel,
      row?.Action,
      row?.OldValue,
      row?.NewValue,
      row?.Comments,
      formatScheduleAuditWhen(row?.At),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  });
}

function buildExportRow(row) {
  const display = resolveScheduleAuditDisplay(row);
  return {
    performedAt: formatScheduleAuditWhen(row?.At),
    action: display.actionDisplay || "",
    field: display.fieldDisplay || "",
    detail: [display.reasonDisplay && `Reason: ${display.reasonDisplay}`, display.detailDisplay]
      .filter(Boolean)
      .join(" · "),
    oldValue: display.oldDisplay || String(row?.OldValue || "").trim(),
    newValue: display.newDisplay || String(row?.NewValue || "").trim(),
    performedBy: display.actorLabel || "",
    source: display.sourceLevel || String(row?.SourceLevel || "").trim(),
  };
}

export async function exportScheduleAuditLogToExcel(rows = [], { studyLabel = "schedule" } = {}) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Activity Log");
  const headers = [
    "Performed At",
    "Action",
    "Field",
    "Detail",
    "Old Value",
    "New Value",
    "Performed By",
    "Source",
  ];

  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" },
    };
  });

  rows.forEach((row) => {
    const entry = buildExportRow(row);
    ws.addRow([
      entry.performedAt,
      entry.action,
      entry.field,
      entry.detail,
      entry.oldValue,
      entry.newValue,
      entry.performedBy,
      entry.source,
    ]);
  });

  ws.columns = headers.map((header) => ({
    header,
    key: header,
    width: Math.max(14, Math.min(42, header.length + 8)),
  }));

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const safeStudy = String(studyLabel || "schedule")
    .replace(/[^\w-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  link.href = url;
  link.download = `schedule-activity-log-${safeStudy || "schedule"}-${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export function hasActiveScheduleAuditFilters(filters = SCHEDULE_ACTIVITY_EMPTY_FILTERS) {
  return (
    Boolean(String(filters.query || "").trim()) ||
    (Array.isArray(filters.fields) && filters.fields.length > 0) ||
    (Array.isArray(filters.users) && filters.users.length > 0) ||
    (filters.dateRange &&
      filters.dateRange.preset !== "All Time" &&
      (filters.dateRange.startDate || filters.dateRange.endDate))
  );
}
